import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { DirectoryEntityType, DirectoryPharmacySearchDto } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { DirectorySearchHelper } from "../Helpers/DirectorySearchHelper";
import { DirectoryAddress } from "./DirectoryAddress";
import { DirectoryFaxNumber } from "./DirectoryFaxNumber";
import { DirectoryPhoneNumber } from "./DirectoryPhoneNumber";
import { IDirectoryEntity } from "./IDirectoryEntity";
import { IGetDisplayName } from "./IGetDisplayName";

export class DirectoryPharmacy implements IGetDisplayName, IDirectoryEntity {
    public type: DirectoryEntityType;
    public id: string;
    public banner: string;
    public pharmacists: string;
    public address: DirectoryAddress;
    public faxNumbers: DirectoryFaxNumber[];
    public phoneNumbers: DirectoryPhoneNumber[];
    public isApproved?: boolean;
    public isSuggestion?: boolean;
    public isInvalid?: boolean;
    public isMylePharmacy?: boolean;
    public mylePharmacyClientTag?: string;

    public constructor(init?: Partial<DirectoryPharmacy>) {
        Object.assign(this, init);
        this.address = new DirectoryAddress(init?.address);
        this.faxNumbers = init?.faxNumbers?.map(p => new DirectoryFaxNumber(p)) ?? [];
        this.phoneNumbers = init?.phoneNumbers?.map(p => new DirectoryPhoneNumber(p)) ?? [];
        this.type = DirectoryEntityType.Pharmacy;
    }

    public getDefaultFaxNumberToSelect(): DirectoryFaxNumber | null {
        return this.faxNumbers.firstOrDefault();
    }

    public static fromSearchDto(dto: DirectoryPharmacySearchDto): DirectoryPharmacy {
        if (!dto)
            return null;

        return new DirectoryPharmacy({
            ...dto,
            address: DirectoryAddress.fromSearchDto(dto.address),
            faxNumbers: [DirectoryFaxNumber.fromSearchDto(dto.faxNumber)],
            phoneNumbers: [DirectoryPhoneNumber.fromSearchDto(dto.phoneNumber)],
            isInvalid: dto.isInvalid
        });
    }

    public getRawFaxNumber(): string {
        return this.faxNumbers?.firstOrDefault() ? this.faxNumbers?.firstOrDefault().number : null;
    }

    public getRawPhoneNumber(): string {
        return this.phoneNumbers?.firstOrDefault() ? this.phoneNumbers?.firstOrDefault().number : null;
    }

    public getDirectoryAddressForDisplay(): string | null {
        return DirectorySearchHelper.formatAddress(this.address);
    }

    public getFaxNumbersForDisplay(): { formattedNumber: string, description?: string }[] {
        const addressCountryId = this.address.countryId;
        const addressStateId = this.address.stateId;

        return this.faxNumbers.map(fax => ({
            formattedNumber: PhoneNumberHelper
                .format(
                    fax?.number,
                    fax?.countryId ?? addressCountryId,
                    cacheStore
                        .GetGeographicsStatesForCountryId(fax?.countryId ?? addressCountryId)
                        ?.some(p => p.Id === addressStateId) ? addressStateId : null
                )
        }));
    }

    public hasFaxNumbers(): boolean {
        return this.faxNumbers.length > 0;
    }

    public hasPhoneNumbers(): boolean {
        return this.phoneNumbers.length > 0;
    }

    public shouldRenderMultipleFaxNumbers(): boolean {
        return false;
    }

    public getFaxNumbersModels(): DirectoryFaxNumber[] {
        return this.faxNumbers;
    }

    public getPhoneNumbersForDisplay(): string[] {
        const addressCountryId = this.address.countryId;
        const addressStateId = this.address.stateId;

        return this.phoneNumbers.map(phone => PhoneNumberHelper
            .format(
                phone?.number,
                phone?.countryId ?? addressCountryId,
                cacheStore
                    .GetGeographicsStatesForCountryId(phone?.countryId ?? addressCountryId)
                    ?.some(p => p.Id === addressStateId) ? addressStateId : null,
                phone?.extension
            ));
    }

    public getLinkedDirectoryEntities(): IDirectoryEntity[] {
        return [];
    }

    public getDisplayName(): string {
        const pharmacyInfo = this.getPharmacyInfo();
        return `${pharmacyInfo.Banner}${pharmacyInfo.Name ? ` - ${pharmacyInfo.Name}` : ""}`;
    }

    public getPharmacyInfo(shouldFormatPhoneAndFax: boolean = true): IPharmacyInfo {
        const phone = this.phoneNumbers?.firstOrDefault();
        const fax = this.faxNumbers?.firstOrDefault();

        return {
            Name: this.pharmacists,
            Banner: this.banner,
            Phone: shouldFormatPhoneAndFax ? PhoneNumberHelper.formatDirectoryNumber(phone) : phone?.number,
            Fax: shouldFormatPhoneAndFax ? PhoneNumberHelper.formatDirectoryNumber(fax) : fax?.number,
            Address: this.address?.toString() ?? "",
            IsMylePharmacy: !!this.isMylePharmacy
        };
    }
}
